// @ts-nocheck
import { forwardRef, useRef, useState } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Button,
  Menu,
  ActionIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { IconDownload, IconChevronDown } from '@tabler/icons-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import { exportChart } from './utils/exportChart';
import type { AreaChartWidgetProps, DateRange } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

const AreaChartWidget = forwardRef<HTMLDivElement, AreaChartWidgetProps>(
  (
    {
      title,
      subtitle,
      headerActions,
      data,
      areas,
      stacked = false,
      height = 300,
      showGrid = true,
      showLegend = true,
      showExport = true,
      onExport,
      xAxisKey = 'date',
      xAxisLabel,
      yAxisLabel,
      dateRange = '30d',
      onDateRangeChange,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const chartRef = useRef<HTMLDivElement>(null);
    const [selectedRange, setSelectedRange] = useState<DateRange>(dateRange);
    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const dateRangeOptions: { value: DateRange; label: string }[] = [
      { value: '7d', label: 'Last 7 days' },
      { value: '30d', label: 'Last 30 days' },
      { value: '90d', label: 'Last 90 days' },
      { value: '1y', label: 'Last year' },
      { value: 'all', label: 'All time' },
    ];

    const handleDateRangeChange = (range: DateRange) => {
      setSelectedRange(range);
      onDateRangeChange?.(range);
    };

    const handleExport = async (format: 'png' | 'svg' | 'csv') => {
      if (onExport) {
        onExport({ format, filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart` });
      } else if (chartRef.current) {
        await exportChart(chartRef.current, data, {
          format,
          filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart`,
        });
      }
    };

    const getCurrentRangeLabel = () => {
      return dateRangeOptions.find((opt) => opt.value === selectedRange)?.label || 'Select range';
    };

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Box>
                {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
                {subtitle && <Text size="sm" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>}
              </Box>

              <Group gap="xs">
                {headerActions}
                {onDateRangeChange && (
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <Button variant="light" size="xs" rightSection={<IconChevronDown size={14} />}>
                        {getCurrentRangeLabel()}
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Date Range</Menu.Label>
                      {dateRangeOptions.map((option) => (
                        <Menu.Item key={option.value} onClick={() => handleDateRangeChange(option.value)}>
                          {option.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                )}

                {showExport && (
                  <Menu shadow="md" width={150}>
                    <Menu.Target>
                      <ActionIcon variant="light" size="lg">
                        <IconDownload size={18} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Export</Menu.Label>
                      <Menu.Item onClick={() => handleExport('png')}>Export as PNG</Menu.Item>
                      <Menu.Item onClick={() => handleExport('svg')}>Export as SVG</Menu.Item>
                      <Menu.Item onClick={() => handleExport('csv')}>Export as CSV</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            </Group>

            <Box ref={chartRef} style={{ width: '100%', height }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  {showGrid && (
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? theme.colors.dark[4] : theme.colors.gray[3]}
                    />
                  )}

                  <XAxis
                    dataKey={xAxisKey}
                    label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
                    stroke={isDark ? theme.colors.dark[2] : theme.colors.gray[6]}
                    tick={{ fill: isDark ? theme.colors.dark[2] : theme.colors.gray[6], fontSize: 12 }}
                  />

                  <YAxis
                    allowDecimals={false}
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
                    stroke={isDark ? theme.colors.dark[2] : theme.colors.gray[6]}
                    tick={{ fill: isDark ? theme.colors.dark[2] : theme.colors.gray[6], fontSize: 12 }}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                      border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                      borderRadius: theme.radius.sm,
                      color: isDark ? theme.colors.dark[0] : theme.colors.gray[9],
                    }}
                    labelStyle={{ color: isDark ? theme.colors.dark[0] : theme.colors.gray[9] }}
                    itemStyle={{ color: isDark ? theme.colors.dark[0] : theme.colors.gray[9] }}
                  />

                  {showLegend && <Legend />}

                  {areas.map((area) => (
                    <Area
                      key={area.dataKey}
                      type="monotone"
                      dataKey={area.dataKey}
                      name={area.name}
                      stroke={area.color}
                      fill={area.color}
                      fillOpacity={area.fillOpacity || 0.6}
                      stackId={stacked ? area.stackId || 'stack' : undefined}
                      animationDuration={1000}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

AreaChartWidget.displayName = 'AreaChartWidget';

export default AreaChartWidget;
